import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { type StateType, type InboxFilters } from '@app/types/reducer-state';

import {
  setExpandInboxSearchBar,
  updateJobIdsInboxFilter,
  updateSearch,
} from '../../actions/job';
import { updateUserProperties } from '../../actions/profile';
import { UPDATE_CHECKED_QUERY_PARAMETERS } from '../../constants/actions';
import { SUCCESS } from '../../constants/general';
import { decompressFilters } from '../../helpers/url-compression';
import {
  generateFiltersFromQueryParams,
  queryParameters,
} from '../../helpers/urls';

const DEFAULT_FILTERS = {
  operatorIds: {},
  groups: {},
  items: {
    myChats: '',
    secondaryOperatorIds: 'My Secondary Assignments',
  },
};

export const useQueryParameters = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const {
    retrievedProfileProperties,
    checkedQueryParameters,
    inboxFilters,
    auth,
  } = useSelector((state: StateType) => ({
    retrievedProfileProperties: state?.profile?.retrievedProfileProperties,
    checkedQueryParameters: state?.profile?.checkedQueryParameters,
    inboxFilters: state?.profile?.properties?.inboxFilters || {},
    auth: state?.auth,
  }));

  /**
   * Update filters from query parameters only on first
   * load of the chats page when the new inbox is enabled
   */
  useEffect(() => {
    const onChatsPage = location?.pathname.split('/').includes('chats');
    if (
      !onChatsPage ||
      checkedQueryParameters === SUCCESS ||
      retrievedProfileProperties !== SUCCESS ||
      !auth?.user
    )
      return;

    const queryParams = new URLSearchParams(location.search);

    // Check for compressed filters parameter
    const compressedFilters = queryParams.get('filters');

    const sortQueryParam = queryParams.get(queryParameters.sort);
    const orderQueryParam = queryParams.get(queryParameters.order);

    // Not persistent
    const searchQueryParam = queryParams.get(queryParameters.search);
    const jobIdsQueryParam = queryParams.getAll(queryParameters.jobIds);

    const updateFilters = async () => {
      let operatorIdsQueryParam: string[] = [];
      let groupIdsQueryParam: string[] = [];
      let tagsQueryParam: string[] = [];
      let myChatsQueryParam: string | null = null;
      let unassignedQueryParam: string | null = null;
      let secondaryChatsQueryParam: string | null = null;
      let refineByQueryParam: string | null = null;

      if (compressedFilters) {
        // Decompress filters
        try {
          const decompressed = await decompressFilters(compressedFilters);
          operatorIdsQueryParam = Array.isArray(decompressed.operatorIds)
            ? decompressed.operatorIds
            : [];
          groupIdsQueryParam = Array.isArray(decompressed.groupIds)
            ? decompressed.groupIds
            : [];
          tagsQueryParam = Array.isArray(decompressed.tags)
            ? decompressed.tags
            : [];
          myChatsQueryParam =
            typeof decompressed.myChats === 'string'
              ? decompressed.myChats
              : null;
          unassignedQueryParam =
            typeof decompressed.unassigned === 'string'
              ? decompressed.unassigned
              : null;
          secondaryChatsQueryParam =
            typeof decompressed.secondaryOperatorIds === 'string'
              ? decompressed.secondaryOperatorIds
              : null;
          refineByQueryParam =
            typeof decompressed.refineBy === 'string'
              ? decompressed.refineBy
              : null;
        } catch (error) {
          console.warn('Failed to decompress filters from URL:', error);
          // Fall back to reading individual parameters
          operatorIdsQueryParam = queryParams.getAll(
            queryParameters.operatorIds,
          );
          groupIdsQueryParam = queryParams.getAll(queryParameters.groupIds);
          tagsQueryParam = queryParams.getAll(queryParameters.tags);
          myChatsQueryParam = queryParams.get(queryParameters.myChats);
          unassignedQueryParam = queryParams.get(queryParameters.unassigned);
          secondaryChatsQueryParam = queryParams.get('secondaryOperatorIds');
          refineByQueryParam = queryParams.get(queryParameters.refineBy);
        }
      } else {
        // Read individual parameters (non-compressed)
        operatorIdsQueryParam = queryParams.getAll(queryParameters.operatorIds);
        groupIdsQueryParam = queryParams.getAll(queryParameters.groupIds);
        tagsQueryParam = queryParams.getAll(queryParameters.tags);
        myChatsQueryParam = queryParams.get(queryParameters.myChats);
        unassignedQueryParam = queryParams.get(queryParameters.unassigned);
        secondaryChatsQueryParam = queryParams.get('secondaryOperatorIds');
        refineByQueryParam = queryParams.get(queryParameters.refineBy);
      }

      const filters = await generateFiltersFromQueryParams(
        {
          operatorIds: operatorIdsQueryParam,
          groupIds: groupIdsQueryParam,
          tags: tagsQueryParam,
          myChats: myChatsQueryParam,
          unassigned: unassignedQueryParam,
          secondaryOperatorIds: secondaryChatsQueryParam,
          refineBy: refineByQueryParam,
          sort: sortQueryParam,
          order: orderQueryParam,
        } as Parameters<typeof generateFiltersFromQueryParams>[0],
        inboxFilters,
      );

      // Type assertion: generateFiltersFromQueryParams returns properly structured objects
      // but TypeScript needs help recognizing the type
      const typedFilters = filters as InboxFilters;

      // Check if the filters are the same as the current filters
      if (JSON.stringify(typedFilters) === JSON.stringify(inboxFilters)) {
        return;
      }

      dispatch(updateUserProperties({ inboxFilters: typedFilters }));
    };

    // There is a query parameter that affects the persistent filter
    // Check for compressed filters or individual filter parameters
    const hasFilterParams =
      compressedFilters ||
      queryParams.getAll(queryParameters.operatorIds).length > 0 ||
      queryParams.getAll(queryParameters.groupIds).length > 0 ||
      queryParams.getAll(queryParameters.tags).length > 0 ||
      queryParams.get(queryParameters.unassigned) ||
      queryParams.get(queryParameters.myChats);

    if (hasFilterParams) {
      updateFilters();
    } else if (Object.keys(inboxFilters).length === 0) {
      if (auth?.user?.firstName && auth?.user?.lastName) {
        DEFAULT_FILTERS.items.myChats = `${auth?.user?.firstName} ${auth?.user?.lastName}`;
        dispatch(updateUserProperties({ inboxFilters: DEFAULT_FILTERS }));
      } else {
        // If auth user is not set, don't update the inbox filters
        return;
      }
    }

    dispatch({ type: UPDATE_CHECKED_QUERY_PARAMETERS });

    // If a search query param is present, load it regardless of persistent filters
    if (searchQueryParam) {
      dispatch(
        updateSearch({
          search: searchQueryParam,
          searchDisplayValue: searchQueryParam,
        }),
      );
      dispatch(setExpandInboxSearchBar(true));
    }
    if (jobIdsQueryParam.length > 0) {
      dispatch(updateJobIdsInboxFilter({ jobIds: jobIdsQueryParam }));
    }
  }, [
    dispatch,
    location?.pathname,
    location.search,
    retrievedProfileProperties,
    checkedQueryParameters,
    inboxFilters,
    auth?.user?.firstName,
    auth?.user?.lastName,
  ]);
};

export default useQueryParameters;
